import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Medicine, PrescriptionDetail, SearchProduct } from "../../interfaces";
import {
  Body as BaseBody,
  Header,
  scrollToTop,
  NavigateBeforeButton,
  ContentContainer as BaseContentContainer,
} from "../../components";
import { Box, styled, Theme, Typography, useMediaQuery } from "@mui/material";
import { SearchedMedicineCard } from "../MedicineSearch/components";
import { useMedicineSearchContext, useDocumentFlow } from "@contexts";
import { generatePrescriptionDetail } from "./utils/prescriptionDetails";
import { MedicineTreatmentForm } from "./components/MedicineTreatmentForm";
import { usePrescriptionInteractions } from "@hooks/useInteractionAlerts";

const MedicineCardContainer = styled(Box)(({ theme }) => ({
  border: "1px solid #eaeaea",
  borderRadius: "8px",
  overflow: "hidden",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: 500,
  },
  alignSelf: "flex-start",
}));

const ContentContainer = styled(BaseContentContainer)(({ theme }) => ({
  alignItems: "flex-start",
  [theme.breakpoints.up("md")]: {
    gap: "16px",
  },
}));

const HeaderBody = styled(BaseBody)(({ theme }) => ({
  alignItems: "flex-start",
  paddingBottom: 0,
  [theme.breakpoints.up("md")]: {
    padding: 24,
    paddingBottom: 0,
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Body = styled(BaseBody)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    gap: "16px",
    padding: 24,
    paddingTop: 0,
    paddingBottom: 0,
    maxWidth: 1200,
  },
  flex: 1,
}));

const Title = styled(Typography)(({ theme }) => ({}));

export function PrescriptionTreatment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchResults, setSearchResults } = useMedicineSearchContext();
  const flow = useDocumentFlow();

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const [settingTreatment, setSettingTreatment] = useState<boolean>(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { medicine }: { medicine: Medicine } = location.state;

  const { activePrinciple, concentration, prescriptionType, unit } = medicine;
  const isSearchProduct = medicine ? "name" in medicine : false;
  const name = isSearchProduct ? (medicine as SearchProduct).name : undefined;
  const medicineQuantity = isSearchProduct
    ? (Number((medicine as SearchProduct).quantity) ?? 30)
    : null;

  const { handleRefetch } = usePrescriptionInteractions(
    flow.enableInteractions ? (activePrinciple ?? "") : "",
  );

  const onSubmit = useCallback(
    async (formData: PrescriptionDetail) => {
      if (hasSubmitted) return;
      setHasSubmitted(true);
      try {
        setSettingTreatment(false);
        scrollToTop();
        setSearchResults(undefined);

        const detail = generatePrescriptionDetail(medicine, formData);
        await flow.submitDetail(detail);
        await handleRefetch();

        navigate(flow.routes.afterSubmitRoute, {
          state: {
            detailAdded: true,
            medicineType: prescriptionType === "HELD" ? "HELD" : "SIMPLE",
          },
        });
      } catch (error) {
        console.error("Error adding prescription detail: ", error);
      } finally {
        setHasSubmitted(false);
      }
    },
    [
      setSearchResults,
      flow,
      medicine,
      setHasSubmitted,
      handleRefetch,
      navigate,
      prescriptionType,
      hasSubmitted,
    ],
  );

  const handleBack = useCallback(() => {
    if (settingTreatment) {
      navigate(flow.routes.treatmentBackRoute, {
        state: { searchResults },
      });
    } else {
      setSettingTreatment(true);
      scrollToTop();
    }
  }, [
    navigate,
    flow.routes.treatmentBackRoute,
    searchResults,
    settingTreatment,
  ]);

  return (
    <>
      <Header
        title={!isDesktop ? flow.labels.treatmentTitle : undefined}
        useMekiDocIcon={isDesktop}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />

      <ContentContainer disableGutters>
        <HeaderBody>
          <Title variant="h5" fontWeight={"medium"}>
            {flow.labels.treatmentTitle}
          </Title>
        </HeaderBody>
        <Body>
          <MedicineCardContainer>
            <SearchedMedicineCard medicine={medicine} isTreatmentCard={true} />
          </MedicineCardContainer>
          <MedicineTreatmentForm
            prefilledMedicineAttributes={{
              activePrinciple: activePrinciple ?? undefined,
              concentration: concentration ?? undefined,
              name,
              unit: unit ?? undefined,
              medicineQuantity,
              prescriptionType,
            }}
            formManagement={{
              onSubmit,
              isLoading: hasSubmitted,
            }}
            submitButtonText={flow.labels.submitButtonText}
          />
        </Body>
      </ContentContainer>
    </>
  );
}
